/**
 * ChoiceChipGroup — a single-select group of compact chips.
 *
 * Chips size to their content and wrap onto a new line when the row runs out
 * of space. Unlike `SegmentedControl` it doesn't measure the window or
 * animate, and unlike `OptionChip` it doesn't flex-fill; it exposes radio
 * semantics so screen readers announce it as one choice among several.
 */
import { Pressable, View } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import "../../theme"; // module augmentation for react-native-unistyles types
import { Text } from "../Text";

export interface ChoiceChipOption<T extends string | number> {
  readonly value: T;
  /** Visible chip text. */
  readonly label: string;
  /** Spoken label when the visible one is abbreviated, e.g. "Monday" for "Mon". */
  readonly accessibilityLabel?: string | undefined;
}

export interface ChoiceChipGroupProps<T extends string | number> {
  readonly options: readonly ChoiceChipOption<T>[];
  /** The selected option's value. */
  readonly value: T;
  readonly onChange: (value: T) => void;
  /** Names the group for assistive technology. */
  readonly accessibilityLabel: string;
  readonly disabled?: boolean | undefined;
  /** `md` is about 32px tall, `sm` about 28px. */
  readonly size?: "sm" | "md" | undefined;
  readonly testID?: string | undefined;
}

function ChoiceChipGroup<T extends string | number>({
  options,
  value,
  onChange,
  accessibilityLabel,
  disabled = false,
  size = "md",
  testID,
}: ChoiceChipGroupProps<T>) {
  const { theme } = useUnistyles();
  // Dark mode's `muted` and `border` match an unchecked chip's `surfaceCard`
  // and `borderCard`, so the checked chip needs its own colours there.
  const checkedColors =
    theme.currentTheme === "dark" ?
      { backgroundColor: theme.colors.grey[700], borderColor: theme.colors.grey[400] }
    : { backgroundColor: theme.colors.muted, borderColor: theme.colors.border };
  return (
    <View
      role="radiogroup"
      aria-label={accessibilityLabel}
      aria-disabled={disabled}
      style={styles.group}
      testID={testID}
    >
      {options.map((option) => {
        const checked = option.value === value;
        return (
          <Pressable
            key={option.value}
            role="radio"
            aria-label={option.accessibilityLabel ?? option.label}
            aria-checked={checked}
            aria-disabled={disabled}
            disabled={disabled}
            onPress={() => {
              if (!checked) onChange(option.value);
            }}
            style={({ pressed }) => [
              styles.chip,
              size === "sm" ? styles.chipSm : styles.chipMd,
              checked && checkedColors,
              pressed && styles.chipPressed,
              disabled && styles.chipDisabled,
            ]}
          >
            <Text textStyle={size === "sm" ? "xs/regular" : "sm/regular"} color="text" numberOfLines={1}>
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

ChoiceChipGroup.displayName = "ChoiceChipGroup";

export { ChoiceChipGroup };

const styles = StyleSheet.create((theme) => ({
  group: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: theme.spacing.md,
  },
  chip: {
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.sm,
    backgroundColor: theme.colors.surfaceCard,
  },
  chipMd: {
    minHeight: theme.spacing["8xl"],
    paddingHorizontal: theme.spacing.xl,
  },
  chipSm: {
    minHeight: theme.spacing["7xl"],
    paddingHorizontal: theme.spacing.lg,
  },
  chipPressed: {
    opacity: 0.8,
  },
  chipDisabled: {
    opacity: 0.5,
  },
}));
